import React from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  resolveEquipmentProcessGroup,
  type EquipmentProcessGroupMeta,
} from "@/pages/system/processes/processGrouping";

export interface ProcessGroupBadgeProps {
  /** Object process hoặc routing step */
  process?: any;
  /** Mã nhóm nếu truyền riêng (ví dụ: 'TRUOC_IN', 'OFFSET_PRINTER', 'SAU_IN') */
  group?: string | null;
  /** Tên công đoạn fallback nếu truyền riêng */
  name?: string | null;
  /** Loại máy mặc định nếu có */
  defaultMachineType?: string | null;
  /** Mã bước công đoạn */
  stepId?: string | null;
  /**
   * Chế độ hiển thị:
   * - true (mặc định): Tên chi tiết theo thiết bị/công đoạn thực tế (In Offset, Cán màng, Bế định hình, Gấp dán hộp...)
   * - false: Tên nhóm thiết bị tổng quát (Cắt xén, In Offset/Flexo, Cán màng/Tráng phủ, Bế dán thành phẩm)
   */
  showDetail?: boolean;
  /** Kích thước: 'xs' | 'sm' | 'default' */
  size?: "xs" | "sm" | "default";
  /** Hiển thị chấm tròn màu nhận diện */
  showDot?: boolean;
  /** Lớp CSS bổ sung */
  className?: string;
}

export function ProcessGroupBadge({
  process,
  group,
  name,
  defaultMachineType,
  stepId,
  showDetail = true,
  size = "default",
  showDot = true,
  className,
}: ProcessGroupBadgeProps) {
  // Bóc tách thông tin công đoạn từ process object hoặc props độc lập
  const item = process || {
    group: group ?? null,
    name: name ?? null,
    defaultMachineType: defaultMachineType ?? null,
    stepId: stepId ?? null,
  };

  // Trường hợp cả process lẫn group đều rỗng hoàn toàn và không có tên
  if (!process && !group && !name && !stepId) {
    return <span className="text-slate-400 text-xs">—</span>;
  }

  const meta: EquipmentProcessGroupMeta = resolveEquipmentProcessGroup(item);
  const displayText = showDetail ? meta.detailName : meta.name;

  const sizeClasses = {
    xs: "text-[9px] px-1.5 py-0 h-4 gap-1",
    sm: "text-[10px] px-2 py-0 h-5 gap-1.5",
    default: "text-[11px] px-2.5 py-0.5 h-6 gap-1.5",
  };

  const tooltipText = `Nhóm thiết bị: ${meta.name} • Phân đoạn: ${meta.stageName}`;

  return (
    <Badge
      variant="outline"
      title={tooltipText}
      className={cn(
        "font-medium border shadow-none inline-flex items-center rounded-full whitespace-nowrap transition-colors",
        sizeClasses[size],
        meta.color,
        className,
      )}
    >
      {showDot && (
        <span
          className={cn(
            "rounded-full shrink-0 inline-block",
            size === "xs" ? "w-1 h-1" : "w-1.5 h-1.5",
            meta.dotColor,
          )}
        />
      )}
      <span>{displayText}</span>
    </Badge>
  );
}

export default ProcessGroupBadge;
